import { toastNotification } from '../../shared/utils/notifications';

const sensors = {
    util: {
        BATTERY_LEVEL: {
            DEAD: 'dead',
            FULL: 'full',
            GOOD: 'good',
            LOW: 'low'
        },
        ERROR_CODES: {
            NO_INTEGRATION: 'NO_INTEGRATION',
            NO_LOGIN: 'NO_LOGIN'
        },
        DECIMAL_PLACES: 1,
        // Numbers are in fahrenheit
        COOLDOWN_PROCESS_TEMPERATURE: {
            STAGE_ONE_STARTING: 140,
            STAGE_ONE_ENDING: 70,
            STAGE_TWO_ENDING: 41
        },
        COOLDOWN_STATUS_FAILED: 'failed',
        COOLDOWN_STATUS_IN_PROGRESS: 'in_progress',
        COOLDOWN_STATUS_SUCCESS: 'success',
        MANUFACTURER: {
            MONNIT: 'monnit',
            LAIRD: 'laird',
            EZURIO: 'ezurio',
            RADIOBRIDGE: 'radiobridge',
            MACHINEQ: 'machineq'
        },
        INTEGRATION_TYPE: {
            SENET: 'senet',
            MACHINEQ: 'machineq',
            MONNIT: 'monnit',
            TEMPALERT: 'tempalert',
            DIGI: 'digi'
        },

        convert_gateway_battery_percent_to_battery_level: function (battery_level) {
            battery_level = parseInt(battery_level, 10);
            var battery_level_display = this.BATTERY_LEVEL.DEAD;

            if (battery_level >= 50) {
                battery_level_display = this.BATTERY_LEVEL.FULL;
            } else if (battery_level < 50 && battery_level >= 20) {
                battery_level_display = this.BATTERY_LEVEL.GOOD;
            } else if (battery_level >= 1 && battery_level < 20) {
                battery_level_display = this.BATTERY_LEVEL.LOW;
            }

            return battery_level_display;
        },

        convert_sensor_battery_percent_to_battery_level: function (battery_level) {
            battery_level = parseInt(battery_level, 10);

            if (battery_level >= 21) {
                return this.BATTERY_LEVEL.FULL;
            }

            return this.BATTERY_LEVEL.LOW;
        },

        get_device_ids_from_scanner: function (result) {
            if (!result) {
                return;
            }
            var barcodes = result.split(':'); // an array of two barcode

            if (barcodes.length !== 2) {
                return;
            }

            return barcodes;
        },

        get_error_string_from_response: function (response, show_error_field) {
            if (!response || !response.errors || !response.errors.length) {
                return undefined;
            }
            if (show_error_field) {
                return `${response.errors[0].message}`;
            }

            return `${response.errors[0].field}: ${response.errors[0].message}`;
        },

        get_sensor_ids_from_scanner: function (result) {
            var barcodes = this.get_device_ids_from_scanner(result);

            return {
                sensor_id: barcodes[0],
                check_digit: barcodes[1]
            };
        },

        get_gateway_ids_from_scanner: function (result) {
            var barcodes = this.get_device_ids_from_scanner(result);

            return {
                external_key: barcodes[0],
                secondary_key: barcodes[1]
            };
        },

        convert_graph_reading_to_company_temperature: function (reading) {
            var temperature_unit = $f.active_user.get('temperature_unit') ?? $f.active_company.get('temperature_unit');
            var output_reading = reading;

            if (temperature_unit === 'C') {
                output_reading = $f.utils.fahrenheit_to_centigrade(reading);
            }

            return $f.utils.round(output_reading, this.DECIMAL_PLACES);
        },

        convert_graph_readings_to_company_temperature: function (readings) {
            var this_ = this;
            return _.map(readings, function (reading) {
                return this_.convert_graph_reading_to_company_temperature(reading);
            });
        },

        get_temperature_for_display: function (temperature) {
            var temperature_unit = $f.active_user.get('temperature_unit') ?? $f.active_company.get('temperature_unit');
            var display_unit = temperature_unit === 'C' ? '°C' : '°F';
            var converted_temperature = this.convert_graph_reading_to_company_temperature(temperature);

            return `${converted_temperature} ${display_unit}`;
        },

        get_cooldown_stage_one_starting_temperature: function () {
            return this.get_temperature_for_display(this.COOLDOWN_PROCESS_TEMPERATURE.STAGE_ONE_STARTING);
        },

        get_cooldown_stage_one_ending_temperature: function () {
            return this.get_temperature_for_display(this.COOLDOWN_PROCESS_TEMPERATURE.STAGE_ONE_ENDING);
        },

        get_cooldown_stage_two_ending_temperature: function () {
            return this.get_temperature_for_display(this.COOLDOWN_PROCESS_TEMPERATURE.STAGE_TWO_ENDING);
        },
        /**
         * Calculates duration between dates and formats
         * format it.
         *
         * @param  {string}  date_start  Django datetime
         * @param  {string}  date_end     Django datetime
         * @return {string} formatted duration or nothing if duration is in seconds.
         * for example: (1 hour 15 minutes), (5 minutes), (2 hours)
         */
        get_duration_between_dates: function (date_start, date_end) {
            var date_started = moment(new Date(date_start));
            var date_ended = moment(new Date(date_end));
            var duration = moment.duration(date_ended.diff(date_started));
            var hours = duration.hours();
            var mins = duration.minutes();
            var has_mins = mins !== 0;
            var has_hours = hours !== 0;
            var mins_display = '';
            var hours_display = '';

            if (!has_mins && !has_hours) {
                return '';
            }

            if (mins === 1) {
                mins_display = _t('mobile_sensors_cooldown_minute');
            }

            if (mins > 1) {
                mins_display = _t('mobile_minutes_plural');
            }

            if (hours === 1) {
                hours_display = _t('mobile_sensors_cooldown_hour');
            }

            if (hours > 1) {
                hours_display = _t('mobile_sensors_cooldown_hour_plural');
            }

            var display = `( ${has_hours ? hours : ''} ${hours_display} ${has_mins ? mins : ''} ${mins_display} )`;

            // remove etxra spaces that happens in the absence of some values
            return display.replace(/\s\s+/g, ' ');
        },

        get_info_from_lorawan_barcode: function (barcode, is_sensor) {
            const alertTime = 5000;
            if (!barcode) {
                toastNotification(_t('mobile_sensors_sensor_deveui_error'), 'error', 'df-alert', alertTime);
                return {};
            }

            if (is_sensor) {
                const sanitized_dev_eui = sanitize_dev_eui(barcode.toUpperCase());
                const sensor_id = sanitized_dev_eui.slice(0, DEV_EUI_REQUIRED_LENGTH);
                if (DEV_EUI_VALID_REGEX.test(sensor_id)) {
                    return {
                        sensor_id: sensor_id
                    };
                }

                // Adds support for RS2xx sensors
                const data = sanitized_dev_eui.split(',');

                if (!is_valid_dev_eui(data[data.length - 1])) {
                    toastNotification(_t('mobile_sensors_sensor_deveui_error'), 'error', 'df-alert', alertTime);
                    return {};
                }

                return {
                    sensor_id: data[data.length - 1]
                };
            }

            // remove colon character when results are displayed as character pairs
            barcode = sanitize_dev_eui(barcode.toUpperCase());

            // split barcodes into array
            const barcodes = barcode.split(',');

            // extract DevEUI from barcode based on format
            const format_match = barcodes.find((each_barcode) => {
                return DEV_EUI_VALID_REGEX.test(each_barcode);
            });

            if (!format_match) {
                toastNotification(_t('mobile_sensors_sensor_deveui_error'), 'error', 'df-alert', alertTime);
                return {};
            }

            return {
                external_key: format_match
            };
        },

        get_info_from_machineq_sensor_barcode: function (barcode) {
            const alertTime = 5000;
            if (!barcode) {
                if ($f.flags.get_flag('fix_machineq_qr')) {
                    toastNotification(_t('mobile_sensors_sensor_deveui_error'), 'error', 'df-alert', alertTime);
                }
                return {};
            }

            const searchParams = new URLSearchParams(barcode);

            if (!searchParams.has('DEVEUI')) {
                if ($f.flags.get_flag('fix_machineq_qr')) {
                    if (barcode.length == DEV_EUI_REQUIRED_LENGTH) {
                        return { sensor_id: barcode.toUpperCase() };
                    }

                    toastNotification(_t('mobile_sensors_sensor_deveui_error'), 'error', 'df-alert', alertTime);
                }

                return {};
            }

            return {
                sensor_id: searchParams.get('DEVEUI').toUpperCase()
            };
        },

        display_battery: function (battery_level) {
            return Number.isInteger(battery_level);
        },

        can_add_gateways: function () {
            return (
                $f.active_company.has_sensor_integration_type(this.INTEGRATION_TYPE.MONNIT) ||
                $f.active_company.has_sensor_integration_type(this.INTEGRATION_TYPE.SENET) ||
                $f.active_company.has_sensor_integration_type(this.INTEGRATION_TYPE.MACHINEQ)
            );
        },

        can_add_sensors: function (account_model) {
            return (
                account_model.has_sensor_integration_type(this.INTEGRATION_TYPE.MONNIT) ||
                $f.active_company.has_sensor_integration_type(this.INTEGRATION_TYPE.SENET) ||
                $f.active_company.has_sensor_integration_type(this.INTEGRATION_TYPE.MACHINEQ)
            );
        },

        get_manufacturer_sensor: function (account_model) {
            if (
                $f.active_company.has_sensor_integration_type(this.INTEGRATION_TYPE.SENET) &&
                !account_model.has_sensor_integration_type(this.INTEGRATION_TYPE.MONNIT) &&
                !$f.active_company.has_sensor_integration_type(this.INTEGRATION_TYPE.MACHINEQ)
            ) {
                // returning integration type, we will receive the specific manufacturer value from the Senet API
                return this.INTEGRATION_TYPE.SENET;
            }

            if (
                account_model.has_sensor_integration_type(this.INTEGRATION_TYPE.MONNIT) &&
                !$f.active_company.has_sensor_integration_type(this.INTEGRATION_TYPE.SENET) &&
                !$f.active_company.has_sensor_integration_type(this.INTEGRATION_TYPE.MACHINEQ)
            ) {
                return this.MANUFACTURER.MONNIT;
            }

            if (
                $f.active_company.has_sensor_integration_type(this.INTEGRATION_TYPE.MACHINEQ) &&
                !account_model.has_sensor_integration_type(this.INTEGRATION_TYPE.MONNIT) &&
                !$f.active_company.has_sensor_integration_type(this.INTEGRATION_TYPE.SENET)
            ) {
                return this.MANUFACTURER.MACHINEQ;
            }

            return null;
        },

        get_manufacturer_gateway: function () {
            const hasSenetIntegration = $f.active_company.has_sensor_integration_type(this.INTEGRATION_TYPE.SENET);
            const hasMonnitIntegration = $f.active_company.has_sensor_integration_type(this.INTEGRATION_TYPE.MONNIT);
            const hasMachineQIntegration = $f.active_company.has_sensor_integration_type(
                this.INTEGRATION_TYPE.MACHINEQ
            );

            if (hasMachineQIntegration && !hasSenetIntegration && !hasMonnitIntegration) {
                return this.INTEGRATION_TYPE.MACHINEQ;
            }

            if (hasSenetIntegration && !hasMonnitIntegration && !hasMachineQIntegration) {
                // returning integration type, we will receive the specific manufacturer value from the Senet API
                return this.INTEGRATION_TYPE.SENET;
            }

            if (hasMonnitIntegration && !hasSenetIntegration && !hasMachineQIntegration) {
                return this.MANUFACTURER.MONNIT;
            }

            // manufacturer will be specified by the gateway selector view
            return null;
        },

        can_select_manufacturer_sensor: function (account_model) {
            const MINIMUM_SENSOR_INTEGRATIONS_FOR_MANUFACTURER_SELECTOR = 2;

            const sensorIntegrationsCounter = [
                $f.active_company.has_sensor_integration_type($f.mobile.sensors.util.INTEGRATION_TYPE.SENET),
                $f.active_company.has_sensor_integration_type($f.mobile.sensors.util.INTEGRATION_TYPE.MACHINEQ),
                account_model.has_sensor_integration_type($f.mobile.sensors.util.INTEGRATION_TYPE.MONNIT)
            ].reduce((counter, sensorIntegrationEnabled) => counter + Number(sensorIntegrationEnabled), 0);

            return sensorIntegrationsCounter >= MINIMUM_SENSOR_INTEGRATIONS_FOR_MANUFACTURER_SELECTOR;
        },

        can_select_manufacturer_gateway: function () {
            const MINIMUM_SENSOR_INTEGRATIONS_FOR_MANUFACTURER_SELECTOR = 2;

            const sensorIntegrationsCounter = [
                $f.active_company.has_sensor_integration_type($f.mobile.sensors.util.INTEGRATION_TYPE.SENET),
                $f.active_company.has_sensor_integration_type($f.mobile.sensors.util.INTEGRATION_TYPE.MACHINEQ),
                $f.active_company.has_sensor_integration_type($f.mobile.sensors.util.INTEGRATION_TYPE.MONNIT)
            ].reduce((counter, sensorIntegrationEnabled) => counter + Number(sensorIntegrationEnabled), 0);

            return sensorIntegrationsCounter >= MINIMUM_SENSOR_INTEGRATIONS_FOR_MANUFACTURER_SELECTOR;
        }
    }
};

export const DEV_EUI_REQUIRED_LENGTH = 16;
export const MACHINEQ_GATEWAY_ID_LENGTH = 16;

// DEV_EUI_REQUIRED_LENGTH + 7 chars(: or space)
// aa:bb:cc:dd:ee:ff:00:11
export const DEV_EUI_REQUIRED_LENGTH_WITH_DELIMITERS = 23;

// Note: Values of all ones or all zeros ( 0xFFFFFFFFFFFF or 0x0) are not valid EUI-64 values
// and should indicate that a value for the EUI-64 is not available (null).
export const DEV_EUI_INVALID_REGEX = new RegExp(`(0{${DEV_EUI_REQUIRED_LENGTH},}|F{${DEV_EUI_REQUIRED_LENGTH},})`, 'i');

export const DEV_EUI_VALID_REGEX = new RegExp(`^[A-F0-9]{${DEV_EUI_REQUIRED_LENGTH}}$`, 'i');
export const DEV_EUI_FIXED_MODULE = 'FFFF';

export const get_device_status_text = (is_enabled, is_online) => {
    if (!is_enabled) {
        return _t('Disabled');
    }
    return is_online ? _t('mobile_sensors_device_online') : _t('mobile_sensors_device_offline');
};

export const get_device_status_class = (is_enabled, is_online) => {
    if (!is_enabled) {
        return 'disabled';
    }

    return is_online ? 'online' : 'offline';
};

export const sanitize_dev_eui = (dev_eui = '') => {
    // remove any ':' and space from the dev_eui
    return dev_eui.replace(/[:\s]/g, '');
};

export const isValidMachineQGatewayId = (gatewayId) => {
    if (!gatewayId) {
        return false;
    }

    if (typeof gatewayId !== 'string') {
        return false;
    }

    const gatewayIdLength = gatewayId.length;

    if (gatewayIdLength !== MACHINEQ_GATEWAY_ID_LENGTH) {
        return false;
    }

    return true;
};

export const is_valid_dev_eui = (dev_eui) => {
    // for more details see:
    // https://lora-developers.semtech.com/library/tech-papers-and-guides/the-book/deveui/
    if (!dev_eui) {
        return false;
    }

    if (typeof dev_eui !== 'string') {
        return false;
    }

    const dev_eui_length = dev_eui.length;

    if (dev_eui_length !== DEV_EUI_REQUIRED_LENGTH) {
        if (dev_eui_length !== DEV_EUI_REQUIRED_LENGTH_WITH_DELIMITERS) {
            return false;
        }
    }

    // remove any ':' and space from the dev_eui
    const dev_eui_sanitized = sanitize_dev_eui(dev_eui);

    if (dev_eui_sanitized.length !== DEV_EUI_REQUIRED_LENGTH) {
        return false;
    }

    if (DEV_EUI_INVALID_REGEX.test(dev_eui_sanitized)) {
        return false;
    }

    // finally check if it only contains hex characters
    return DEV_EUI_VALID_REGEX.test(dev_eui_sanitized);
};

export default sensors;
